import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import ChatInput from "../MissgoAIChatbot/components/ChatInput";
import ChatLog, { ChatLogHandle } from "../MissgoAIChatbot/components/ChatLog";
import { useGetFreeWhetherToUse, useGetLawChatLog, useGetLawChatUsage, useGetStreamAIChatAnswer } from "../../api";
import { getUserSubs } from "../../utils/useGetSubsState";

interface MessageData {
    role: "user" | "assistant";
    content: string;
}

interface OwnProps {
    onPaymentButtonClick?: (type?: "upgrade" | "ticket") => void;
}

const INITIAL_MESSAGE: MessageData[] = [
    {
        role: "assistant",
        content: "안녕하세요. 미스고 AI 법령 도우미 입니다.\n 법에 관련한것은 무엇이든 물어보세요!",
    },
    {
        role: "assistant",
        content: "※ AI 법령 도우미는 정보 제공 목적이며 법률상담이 아닙니다. 실제 판단 및 해석은 전문가와 상의하시기 바랍니다",
    },
];

const MissgoAILawChatbot = ({ onPaymentButtonClick }: OwnProps) => {
    const searchParams = new URLSearchParams(location.search);
    const itemId = searchParams.get("itemId") ?? "";
    const userSubs = getUserSubs();

    const [chatData, setChatData] = useState<MessageData[]>(INITIAL_MESSAGE);
    const [hasFirstStreamCompleted, setHasFirstStreamCompleted] = useState<boolean>(false);

    const { startStream, isLoading: isStreaming, cancelStream } = useGetStreamAIChatAnswer("law");
    const {
        data: beforeChatLogData,
        isLoading: beforeChatLogLoading,
        refetch: refetchBeforeChatLog,
    } = useGetLawChatLog(itemId, {
        enabled: !!itemId,
        onSuccess: (data) => {
            if (data?.data && chatData.length === INITIAL_MESSAGE.length) {
                setChatData([...INITIAL_MESSAGE, ...(data.data as MessageData[])]);
            }
        },
    });
    const { data: lawChatUsage, refetch: refetchLawChatUsage } = useGetLawChatUsage();

    const chatLogRef = useRef<ChatLogHandle>(null);

    const isOutOfUsage = lawChatUsage?.remaining === 0;

    useEffect(() => {
        refetchBeforeChatLog();
        refetchLawChatUsage();
    }, []);

    const handleInputFocus = () => {
        chatLogRef.current?.scrollToBottom();
    };

    const handleSendMessage = (message: string) => {
        if (isStreaming) return;

        const userMessage: MessageData = { role: "user", content: message };
        const assistantPlaceholder: MessageData = { role: "assistant", content: "" };

        setChatData((prevData) => [...prevData, userMessage, assistantPlaceholder]);

        startStream(
            { itemId, question: message },
            {
                onChunk: (chunk) => {
                    setChatData((prevData) => {
                        const updatedData = [...prevData];
                        const lastMessageIndex = updatedData.length - 1;
                        if (lastMessageIndex >= 0 && updatedData[lastMessageIndex].role === "assistant") {
                            updatedData[lastMessageIndex].content += chunk;
                        }
                        return updatedData;
                    });
                },
                onDone: () => {
                    if (!hasFirstStreamCompleted) setHasFirstStreamCompleted(true);

                    setChatData((prevData) => {
                        const updatedData = [...prevData];
                        const lastMessageIndex = updatedData.length - 1;
                        if (lastMessageIndex < 0) return updatedData;
                        const finalContent = updatedData[lastMessageIndex].content;
                        if (finalContent.endsWith("\n")) {
                            updatedData[lastMessageIndex].content = finalContent.slice(0, -1);
                        }
                        return updatedData;
                    });

                    setHasFirstStreamCompleted(false);
                    refetchLawChatUsage();
                },
                onError: () => {
                    setChatData((prevData) => {
                        const updatedData = [...prevData];
                        const lastMessageIndex = updatedData.length - 1;
                        if (lastMessageIndex >= 0) {
                            updatedData[lastMessageIndex].content = "오류가 발생했습니다.";
                        }
                        return updatedData;
                    });

                    if (!hasFirstStreamCompleted) setHasFirstStreamCompleted(true);
                    refetchLawChatUsage();
                },
            },
        );
    };

    const showLoading = beforeChatLogLoading || (isStreaming && !hasFirstStreamCompleted);

    const formatDate = (expiredDay: string) => {
        const date = new Date(expiredDay);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    };

    return (
        <MissgoAILawChatbotContainer style={{ height: "calc(100vh - 140px)" }}>
            <ChatLog ref={chatLogRef} data={chatData} isLoading={showLoading} />

            {isOutOfUsage && (
                <div className="sidePadding14">
                    <TicketWarningBox>
                        <div>사용 횟수가 모두 소진되었습니다.</div>
                        <TicketButtonBox>
                            <button onClick={() => onPaymentButtonClick?.("upgrade")}>구독권 업그레이드</button>
                        </TicketButtonBox>
                    </TicketWarningBox>
                </div>
            )}

            <ChatInputContainer>
                <ChatInputBox>
                    <ChatInput
                        onSubmit={handleSendMessage}
                        onCancel={cancelStream}
                        isLoading={isStreaming}
                        isDisabled={isOutOfUsage}
                        onFocus={handleInputFocus}
                    />
                </ChatInputBox>
            </ChatInputContainer>
        </MissgoAILawChatbotContainer>
    );
};

const MissgoAILawChatbotContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    gap: 12px;
`;

const ChatInputContainer = styled.div`
    width: calc(100% - 26px);
    box-sizing: border-box;
    position: fixed;
    bottom: 0;
    left: 14px;
    right: 14px;
    background-color: white;
`;

const ChatInputBox = styled.div`
    width: 100%;
`;

const ChatRemainingCount = styled.div`
    width: calc(100% - 8px);
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 30px;
    box-sizing: border-box;
    padding: 8px;
    text-align: center;
    margin: 0 4px;

    border-radius: 4px;
    border: 1px solid #000;
    background: #fff;

    color: #000;
    font-size: 12px;
    font-weight: 400;
    line-height: 140%;
`;

const TicketWarningBox = styled.div`
    width: calc(100% - 8px);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    box-sizing: border-box;

    border-radius: 5px;
    background: #000;

    color: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 140%;
`;

const TicketButtonBox = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 8px;

    button {
        cursor: pointer;
        padding: 5px 12px;
        border-radius: 30px;
        background-color: #fff;
        color: #000;
        font-size: 10px;
        font-weight: 400;
        line-height: 140%;
    }
`;

export default MissgoAILawChatbot;
